import { create } from "zustand";
import { STORAGE_KEYS } from "@/shared/lib/constants";
import { createLogger } from "@/shared/lib/logger";
import type { Account, User } from "./types";

const log = createLogger("AUTH_STORE");

// ============================================================
// State shape
// ============================================================

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  account: Account | null;
  user: User | null;
  roles: string[];
  sessionId: number | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
}

interface AuthActions {
  setTokens: (access: string, refresh: string) => void;
  setAuth: (data: {
    access: string;
    refresh: string;
    account: Account;
    roles: string[];
    user: User | null;
    sessionId: number;
  }) => void;
  setUser: (user: User) => void;
  setHydrated: () => void;
  logout: () => void;
}

export type AuthStore = AuthState & AuthActions;

// ============================================================
// Store
// ============================================================

export const useAuthStore = create<AuthStore>()((set) => ({
  accessToken: null,
  refreshToken: null,
  account: null,
  user: null,
  roles: [],
  sessionId: null,
  isAuthenticated: false,
  isHydrated: false,

  setTokens: (access, refresh) => {
    log.debug("setTokens called");
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
    set({ accessToken: access, refreshToken: refresh, isAuthenticated: true });
  },

  setAuth: ({ access, refresh, account, roles, user, sessionId }) => {
    log.info("setAuth — user authenticated", { accountId: account.id, roles });
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
    set({
      accessToken: access,
      refreshToken: refresh,
      account,
      user,
      roles,
      sessionId,
      isAuthenticated: true,
    });
  },

  setUser: (user) => {
    log.debug("setUser", { userId: user.id });
    set({ user });
  },

  setHydrated: () => {
    log.debug("Hydration complete");
    set({ isHydrated: true });
  },

  logout: () => {
    log.info("Logout — clearing auth state");
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    set({
      accessToken: null,
      refreshToken: null,
      account: null,
      user: null,
      roles: [],
      sessionId: null,
      isAuthenticated: false,
    });
  },
}));

/**
 * Non-reactive access for interceptors (no re-render).
 */
export const getAccessToken = () => useAuthStore.getState().accessToken;
export const getRefreshToken = () =>
  useAuthStore.getState().refreshToken ||
  sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
